// Ficha de la propuesta viva del lado del ejecutivo: los cinco puntos del
// intake (¿la abrió?, tiempo por sección, productos más vistos, ¿la reenvió?,
// ¿aceptó o pidió cambios?), envío de la liga, versión nueva y resumen por IA.
import * as React from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, ExternalLink } from "lucide-react";
import { Boton, Campo, Dialog, DialogContenido, DialogDescripcion, DialogDisparador, DialogEncabezado, DialogPie, DialogTitulo, EsqueletoTabla, Input, Pill, PuntoNaranja, Textarea, TituloPagina, useToast, Vacio } from "@iavmx/ui";
import { ErrorApi, leer } from "../api";
import { Barra, BloqueIa, Cifra, ErrorEnLinea, FilaTiempo, Miga, Seccion } from "../componentes";
import { useNombresProducto, useSalud } from "../datos";
import { duracion, fechaCorta, fechaHora, mxn, relativo } from "../formato";
import { useSesion } from "../sesion";
import { ETIQUETA_ESTADO_PROPUESTA, type RespuestaPropuesta, type SeccionVersion } from "../tipos";
import { TONO_PROPUESTA } from "./Propuestas";

function Enviar({ id, contactos, estado, onListo }: { id: string; contactos: { id: string; nombre: string; correo: string | null; telefono: string | null }[]; estado: string; onListo: (liga: string) => void }) {
  const { api } = useSesion();
  const [abierto, setAbierto] = React.useState(false);
  const [canal, setCanal] = React.useState<"correo" | "whatsapp" | "liga">("correo");
  const [contactoId, setContactoId] = React.useState(contactos[0]?.id ?? "");
  const m = useMutation({
    mutationFn: () => api.api.propuestas[":id"].enviar.$post({ param: { id }, json: { canal, contactoId: contactoId || null } }).then((r) => leer<{ ok: boolean; liga: string }>(r)),
    onSuccess: (r) => {
      setAbierto(false);
      onListo(r.liga);
    },
  });
  const primera = estado === "borrador";
  return (
    <Dialog open={abierto} onOpenChange={setAbierto}>
      <DialogDisparador asChild>
        <Boton variante={primera ? "acento" : "contorno"} tamano="sm">
          {primera ? "Enviar propuesta" : "Reenviar liga"}
        </Boton>
      </DialogDisparador>
      <DialogContenido>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate();
          }}
        >
          <DialogEncabezado>
            <DialogTitulo>{primera ? "Enviar la propuesta" : "Reenviar la liga"}</DialogTitulo>
            <DialogDescripcion>La liga se copia para que la mandes por el canal que elijas; queda registrado en la oportunidad. El correo y WhatsApp automáticos llegan cuando esas integraciones estén conectadas.</DialogDescripcion>
          </DialogEncabezado>
          <fieldset>
            <legend className="mb-1.5 block font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Canal</legend>
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  ["correo", "Correo"],
                  ["whatsapp", "WhatsApp"],
                  ["liga", "Solo copiar la liga"],
                ] as const
              ).map(([v, t]) => (
                <label key={v} className={`inline-flex h-8 cursor-pointer items-center rounded-full border px-3 font-ui text-xs font-semibold ${canal === v ? "border-tinta bg-tinta text-tinta-inversa" : "border-linea-fuerte text-tinta-2"}`}>
                  <input type="radio" name="canal" className="sr-only" checked={canal === v} onChange={() => setCanal(v)} />
                  {t}
                </label>
              ))}
            </div>
          </fieldset>
          {contactos.length > 0 && (
            <Campo etiqueta="Para" htmlFor="pc">
              <select id="pc" value={contactoId} onChange={(e) => setContactoId(e.target.value)} className="flex h-10 w-full border border-input bg-fondo px-3 font-ui text-base text-tinta sm:text-sm">
                <option value="">Sin contacto específico</option>
                {contactos.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                    {c.correo ? ` · ${c.correo}` : c.telefono ? ` · ${c.telefono}` : ""}
                  </option>
                ))}
              </select>
            </Campo>
          )}
          <ErrorEnLinea error={m.error} />
          <DialogPie>
            <Boton type="button" variante="contorno" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" disabled={m.isPending}>
              {m.isPending ? "Enviando…" : "Marcar como enviada y copiar liga"}
            </Boton>
          </DialogPie>
        </form>
      </DialogContenido>
    </Dialog>
  );
}

function textoDe(html: string): string {
  return html
    .replace(/<\/p>\s*<p>/g, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .trim();
}

function NuevaVersion({ id, version, titulo, vigencia, onListo }: { id: string; version: { secciones: SeccionVersion[]; condiciones: string | null } | null; titulo: string; vigencia: string | null; onListo: () => void }) {
  const { api } = useSesion();
  const [abierto, setAbierto] = React.useState(false);
  const [t, setT] = React.useState(titulo);
  const [v, setV] = React.useState(vigencia ?? "");
  const [secciones, setSecciones] = React.useState(() => (version?.secciones ?? []).map((s) => ({ clave: s.clave, titulo: s.titulo, texto: textoDe(s.html), generada: s.clave === "lo-que-proponemos" || s.clave === "inversion" })));
  const [condiciones, setCondiciones] = React.useState(version?.condiciones ?? "");
  const m = useMutation({
    mutationFn: () =>
      api.api.propuestas[":id"].version
        .$put({ param: { id }, json: { titulo: t.trim(), vigencia: v || null, condiciones: condiciones || null, secciones: secciones.map((s) => ({ clave: s.clave, titulo: s.titulo, texto: s.texto })) } })
        .then((r) => leer<{ ok: boolean; numero: number }>(r)),
    onSuccess: () => {
      setAbierto(false);
      onListo();
    },
  });
  return (
    <Dialog open={abierto} onOpenChange={setAbierto}>
      <DialogDisparador asChild>
        <Boton variante="contorno" tamano="sm">
          Nueva versión
        </Boton>
      </DialogDisparador>
      <DialogContenido className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate();
          }}
        >
          <DialogEncabezado>
            <DialogTitulo>Nueva versión de la propuesta</DialogTitulo>
            <DialogDescripcion>Se publica en la misma liga con la fecha de la versión. Las partidas y la inversión vienen de la cotización; para cambiar precios, edita la cotización.</DialogDescripcion>
          </DialogEncabezado>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_160px]">
            <Campo etiqueta="Título" htmlFor="vt">
              <Input id="vt" value={t} onChange={(e) => setT(e.target.value)} required className="text-base sm:text-sm" />
            </Campo>
            <Campo etiqueta="Vigente hasta" htmlFor="vv">
              <Input id="vv" type="date" value={v} onChange={(e) => setV(e.target.value)} className="text-base sm:text-sm" />
            </Campo>
          </div>
          {secciones.map((s, i) => (
            <Campo key={s.clave} etiqueta={s.titulo} htmlFor={`s-${s.clave}`} ayuda={s.generada ? "Generada desde la cotización; no se edita aquí." : "Texto libre; un párrafo por línea en blanco."}>
              <Textarea id={`s-${s.clave}`} value={s.generada ? "(partidas e inversión de la cotización)" : s.texto} disabled={s.generada} onChange={(e) => setSecciones((xs) => xs.map((x, j) => (j === i ? { ...x, texto: e.target.value } : x)))} className="min-h-20 text-base sm:text-sm" />
            </Campo>
          ))}
          <Campo etiqueta="Condiciones" htmlFor="vc">
            <Textarea id="vc" value={condiciones} onChange={(e) => setCondiciones(e.target.value)} className="min-h-16 text-base sm:text-sm" />
          </Campo>
          <ErrorEnLinea error={m.error} />
          <DialogPie>
            <Boton type="button" variante="contorno" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" disabled={m.isPending}>
              {m.isPending ? "Publicando…" : "Publicar versión"}
            </Boton>
          </DialogPie>
        </form>
      </DialogContenido>
    </Dialog>
  );
}

const TITULO_SECCION: Record<string, string> = { "lo-que-buscas": "Lo que buscas", "lo-que-proponemos": "Lo que proponemos", inversion: "Inversión", calendario: "Calendario", condiciones: "Condiciones", "sin-seccion": "Encabezado" };
const TIPO_EVENTO: Record<string, string> = { apertura: "Apertura", reenvio: "Probable reenvío", aceptacion: "Aceptó la propuesta", cambios: "Pidió cambios", recordatorio: "Recordatorio", seccion: "Lectura" };

export function Propuesta() {
  const { id } = useParams({ from: "/propuestas/$id" });
  const { api, listo } = useSesion();
  const qc = useQueryClient();
  const { avisar } = useToast();
  const salud = useSalud();
  const nombreDe = useNombresProducto();
  const q = useQuery({ queryKey: ["propuesta", id], queryFn: () => api.api.propuestas[":id"].$get({ param: { id } }).then((r) => leer<RespuestaPropuesta>(r)), enabled: listo, refetchInterval: 30_000 });
  const recargar = () => {
    void qc.invalidateQueries({ queryKey: ["propuesta", id] });
    void qc.invalidateQueries({ queryKey: ["propuestas"] });
  };
  const [resumen, setResumen] = React.useState<{ queVio: string; queNoVio: string; siguientePaso: string } | null>(null);
  const resumir = useMutation({
    mutationFn: () => api.api.propuestas[":id"]["resumen-ia"].$post({ param: { id } }).then((r) => leer<{ resumen: { queVio: string; queNoVio: string; siguientePaso: string } }>(r)),
    onSuccess: (r) => setResumen(r.resumen),
    onError: (e) => avisar({ titulo: e instanceof ErrorApi && e.status === 503 ? "IA sin configurar" : "No se pudo resumir", descripcion: e.message, tono: "error" }),
  });
  const copiar = async (liga: string) => {
    try {
      await navigator.clipboard.writeText(liga);
      avisar({ titulo: "Liga copiada", descripcion: liga, tono: "exito" });
    } catch {
      avisar({ titulo: "Copia la liga a mano", descripcion: liga });
    }
  };

  if (q.isLoading) return <EsqueletoTabla />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo abrir la propuesta" texto={q.error?.message} />;
  const { propuesta: p, rastreo: r, recorrido, contactos, ia } = q.data;
  const ligaCorta = p.liga.replace(/^https?:\/\//, "");
  const seccionMax = r.porSeccion[0];
  const nuevosDispositivos = r.visitantes.slice(1);

  return (
    <>
      <Miga partes={[{ texto: "Propuestas", to: "/propuestas" }, { texto: p.cuenta, to: "/cuentas/$id", params: { id: p.cuentaId } }, { texto: p.id }]} />
      <TituloPagina
        descripcion={`${p.cuenta}${p.enviadaEn ? ` · enviada el ${fechaCorta(p.enviadaEn)}${p.canalEnvio ? ` por ${p.canalEnvio}` : ""}` : " · sin enviar"}${p.total ? ` · ${mxn(Number(p.total) * 1.16)} con IVA` : ""} · versión ${p.versionActual}`}
        accion={
          <div className="flex flex-wrap gap-2">
            <Boton variante="fantasma" tamano="sm" asChild>
              <a href={`${p.liga}?p=1`} target="_blank" rel="noreferrer">
                <ExternalLink /> Vista previa
              </a>
            </Boton>
            <Boton variante="fantasma" tamano="sm" onClick={() => void copiar(p.liga)}>
              <Copy /> Copiar liga
            </Boton>
            <NuevaVersion key={p.versionActual} id={p.id} version={p.version} titulo={p.titulo} vigencia={p.vigencia} onListo={() => { avisar({ titulo: "Versión publicada en la misma liga", tono: "exito" }); recargar(); }} />
            {p.estado !== "aceptada" && (
              <Enviar
                id={p.id}
                contactos={contactos}
                estado={p.estado}
                onListo={(liga) => {
                  void copiar(liga);
                  recargar();
                }}
              />
            )}
            {p.oportunidadId && p.estado !== "borrador" && (
              <Boton variante="acento" tamano="sm" asChild>
                <Link to="/oportunidades/$id" params={{ id: p.oportunidadId }}>
                  Programar próxima acción
                </Link>
              </Boton>
            )}
          </div>
        }
      >
        {p.titulo}
        <PuntoNaranja />
      </TituloPagina>
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Pill tono={TONO_PROPUESTA[p.estado] ?? "neutro"}>{ETIQUETA_ESTADO_PROPUESTA[p.estado]}</Pill>
        <a href={p.liga} target="_blank" rel="noreferrer" className="break-all font-ui text-xs text-azul hover:underline">
          {ligaCorta}
        </a>
        {p.vigencia && <span className="font-ui text-xs text-tinta-tenue">Vigente hasta el {fechaCorta(p.vigencia)}</span>}
        {p.estado === "borrador" && <span className="font-ui text-xs text-tinta-tenue">La liga solo abre en vista previa hasta que la envíes.</span>}
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 border-y border-linea py-4 sm:grid-cols-5">
        <Cifra valor={r.abierta ? "Sí" : "No"} leyenda={r.abierta ? `¿La abrió? · ${fechaHora(r.primeraAperturaEn)}` : "¿La abrió? · todavía no"} />
        <Cifra valor={r.aperturas} leyenda={`apertura${r.aperturas === 1 ? "" : "s"} · ${r.visitantes.length} dispositivo${r.visitantes.length === 1 ? "" : "s"}`} />
        <Cifra valor={r.segundosTotales ? duracion(r.segundosTotales) : "—"} leyenda={seccionMax ? `leídos · más en ${TITULO_SECCION[seccionMax.seccion] ?? seccionMax.seccion}` : "leídos en total"} />
        <Cifra valor={r.reenviada ? "Sí" : "No"} leyenda={r.reenviada ? `¿La reenvió? · dispositivo nuevo ${relativo(nuevosDispositivos[0]?.primeraVez)}` : "¿La reenvió? · un solo dispositivo"} />
        <Cifra valor={r.aceptadaEn ? "Sí" : r.cambios.length ? "Cambios" : "—"} leyenda={r.aceptadaEn ? `¿Aceptó? · ${fechaHora(r.aceptadaEn)}` : r.cambios.length ? `pidió cambios ${relativo(r.cambios.at(-1)!.en)}` : "¿Aceptó? · sin respuesta aún"} />
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="min-w-0 space-y-8">
          {r.cambios.length > 0 && (
            <section className="border border-ambar/40 bg-ambar/5 p-4">
              <h2 className="font-titulo text-base font-bold">Lo que pidió cambiar</h2>
              <ul className="mt-2 space-y-1 font-ui text-sm">
                {r.cambios.map((c, i) => (
                  <li key={i}>
                    <span className="text-tinta-tenue">{fechaHora(c.en)} · </span>
                    {c.comentario ? `«${c.comentario}»` : "Sin comentario. Llama para entender qué cambiaría."}
                  </li>
                ))}
              </ul>
              <p className="mt-2 font-ui text-xs text-tinta-tenue">Publica una versión nueva en esta misma liga; el estado vuelve a «enviada».</p>
            </section>
          )}

          <BloqueIa
            titulo="Qué leyó y qué sigue · IA"
            pie={
              ia && salud.data?.ia ? (
                <Boton variante="liga" tamano="sm" onClick={() => resumir.mutate()} disabled={resumir.isPending || !r.abierta}>
                  {resumir.isPending ? "Resumiendo…" : resumen ? "Volver a resumir" : "Generar resumen"}
                </Boton>
              ) : (
                "IA sin configurar en este entorno (falta OPENAI_API_KEY): el resumen se genera cuando esté conectada. Los datos de abajo son los reales."
              )
            }
          >
            {resumen ? (
              <dl className="space-y-2">
                <div>
                  <dt className="font-semibold">Qué vio</dt>
                  <dd>{resumen.queVio}</dd>
                </div>
                <div>
                  <dt className="font-semibold">Qué no vio</dt>
                  <dd>{resumen.queNoVio}</dd>
                </div>
                <div>
                  <dt className="font-semibold">Siguiente paso</dt>
                  <dd>{resumen.siguientePaso}</dd>
                </div>
              </dl>
            ) : r.abierta ? (
              <p>
                {r.visitantes[0] ? `Se abrió desde ${r.visitantes[0].dispositivo}` : "Se abrió"}
                {r.aperturas > 1 ? ` y volvió ${r.aperturas - 1} ${r.aperturas - 1 === 1 ? "vez" : "veces"}` : ""}
                {seccionMax ? `; donde más tiempo pasó fue en ${TITULO_SECCION[seccionMax.seccion] ?? seccionMax.seccion} (${duracion(seccionMax.segundos)})` : ""}.
                {r.reenviada ? " Un dispositivo nuevo la abrió después: probable reenvío a quien aprueba." : ""}
                {r.aceptadaEn ? " Ya la aceptó: confirma fechas y pasa a facturación." : r.cambios.length ? " Pidió cambios: responde con una versión nueva." : " Una llamada al día siguiente de la lectura cierra mejor que un correo."}
              </p>
            ) : (
              <p>Todavía no se abre. Si pasan tres días sin apertura, aparece en el tablero como propuesta sin abrir.</p>
            )}
          </BloqueIa>

          <Seccion titulo="Recorrido">
            {recorrido.length === 0 && !p.enviadaEn ? (
              <p className="font-ui text-sm text-tinta-tenue">El recorrido empieza cuando la envíes.</p>
            ) : (
              <ul>
                {p.enviadaEn && <FilaTiempo titulo="Enviada" detalle={`${p.canalEnvio ?? "liga"} · desde la cotización ${p.folio ? `COT-${String(p.folio).padStart(4, "0")}` : ""}`} en={p.enviadaEn} />}
                {recorrido.map((e) => {
                  const v = r.visitantes.find((x) => x.hash === e.visitanteHash);
                  const detalle = [v?.dispositivo, e.pais, e.tipo === "cambios" && e.detalle?.comentario ? `«${String(e.detalle.comentario)}»` : null].filter(Boolean).join(" · ");
                  return <FilaTiempo key={e.id} titulo={TIPO_EVENTO[e.tipo] ?? e.tipo} detalle={detalle} en={e.en} azul={e.tipo === "reenvio"} />;
                })}
              </ul>
            )}
          </Seccion>
        </div>

        <aside className="min-w-0 space-y-8">
          <Seccion titulo="Tiempo por sección">
            {r.porSeccion.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Sin lectura registrada todavía.</p>
            ) : (
              <ul className="space-y-2">
                {(p.version?.secciones.map((s) => s.clave) ?? []).concat("condiciones").map((clave) => {
                  const s = r.porSeccion.find((x) => x.seccion === clave) ?? { seccion: clave, segundos: 0, pct: 0 };
                  return (
                    <li key={clave} className="grid grid-cols-[130px_minmax(0,1fr)_56px] items-center gap-2 font-ui text-sm">
                      <span className="truncate">{p.version?.secciones.find((x) => x.clave === clave)?.titulo ?? TITULO_SECCION[clave] ?? clave}</span>
                      <Barra pct={s.pct} etiqueta={`${s.pct} % del tiempo`} />
                      <span className="text-right tabular-nums text-tinta-3">{duracion(s.segundos)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            {r.visitantes.length > 1 && <p className="font-ui text-xs text-tinta-tenue">Suma de todos los dispositivos ({r.visitantes.length}).</p>}
          </Seccion>
          <Seccion titulo="Productos más vistos">
            {r.productos.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Se registra cuando el cliente se detiene en una partida de «Lo que proponemos».</p>
            ) : (
              <ul className="space-y-1.5">
                {r.productos.map((x) => (
                  <li key={x.producto} className="flex justify-between gap-2 font-ui text-sm">
                    <span>{nombreDe(x.producto)}</span>
                    <span className="tabular-nums text-tinta-3">{duracion(x.segundos)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
          <Seccion titulo="Dispositivos">
            {r.visitantes.length === 0 ? (
              <p className="font-ui text-sm text-tinta-tenue">Nadie la ha abierto.</p>
            ) : (
              <ul className="space-y-1.5 font-ui text-sm">
                {r.visitantes.map((v, i) => (
                  <li key={v.hash} className="flex justify-between gap-2">
                    <span>
                      {i === 0 ? "Primero" : "Nuevo"} · {v.dispositivo}
                    </span>
                    <span className="tabular-nums text-tinta-3">
                      {v.aperturas}× · {duracion(v.segundos)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="font-ui text-xs text-tinta-tenue">Nunca guardamos la IP ni identificamos a la persona: solo el tipo de dispositivo y un hash.</p>
          </Seccion>
          <Seccion titulo="Avisos">
            <p className="font-ui text-sm text-tinta-3">Recibes un aviso en la bandeja{salud.data?.correo ? " y por correo" : ""} al abrirse por primera vez, al abrirse desde otro dispositivo y al aceptar o pedir cambios. El subgerente recibe el resumen diario.</p>
            {!salud.data?.correo && <p className="font-ui text-xs text-tinta-tenue">Correo sin configurar (RESEND_API_KEY): por ahora solo en la bandeja.</p>}
          </Seccion>
        </aside>
      </div>
    </>
  );
}
